import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../base-query';
import { serializeQueryArgs } from '../serialize-query-args';
import { TAX_SCOPE } from '../../services/api/tax-category/TaxCategoriesManager';

export const TAX_CATEGORIES_TAGS = Object.freeze({
  LIST: 'tax_categories_list',
});

/**
 * IndexTaxCategoriesController returns TaxCategoryResource::collection(...)
 * without withoutWrapping(), so the JSON body is Laravel's default
 * { data: [...] } envelope — unwrap it the same way ManagersApiSlice does.
 */
const unwrapTaxCategoriesResponse = (response) => (Array.isArray(response?.data) ? response.data : (response ?? []));

/**
 * Same wrapping quirk as the collection above, but for a single
 * TaxCategoryResource: the store/update controllers don't opt out of
 * Laravel's default wrapping either, so the body is { data: {...} } rather
 * than the bare resource. Unwrapped here so callers (e.g. an inline "create a
 * tax category" picker reading the new id) don't have to know about it.
 */
const unwrapTaxCategoryResponse = (response) => response?.data ?? response ?? null;

/**
 * Same /tax-categories manager methods back both the company screen and the
 * partner (service provider) screen — only `scope` differs, which decides
 * whether TaxCategoriesManager blanks x-on-behalf-of-company. Tag by scope so
 * a save on one screen never invalidates the other's cached list.
 */
export const taxCategoriesApiSlice = createApi({
  reducerPath: 'taxCategoriesApiSlice',
  baseQuery,
  tagTypes: Object.values(TAX_CATEGORIES_TAGS),
  endpoints: (builder) => ({
    getTaxCategories: builder.query({
      query: ({ apiManager, ...rest }) => apiManager.index(rest),
      transformResponse: unwrapTaxCategoriesResponse,
      providesTags: (_result, _error, { scope }) => [{ type: TAX_CATEGORIES_TAGS.LIST, id: scope }],
    }),
    createTaxCategory: builder.mutation({
      query: ({ apiManager, ...rest }) => apiManager.create(rest),
      transformResponse: unwrapTaxCategoryResponse,
      invalidatesTags: (_result, error, { scope }) => {
        if (error) {
          return [];
        }

        return [{ type: TAX_CATEGORIES_TAGS.LIST, id: scope }];
      },
    }),
    /**
     * Never cached: the count is read right before a save and echoed back, so a
     * remembered one is the one thing it must not serve.
     */
    getTaxCategoryImpact: builder.query({
      query: ({ apiManager, ...rest }) => apiManager.impact(rest),
      transformResponse: unwrapTaxCategoryResponse,
      keepUnusedDataFor: 0,
    }),
    updateTaxCategory: builder.mutation({
      query: ({ apiManager, ...rest }) => apiManager.update(rest),
      // Both screens, unlike the other writes: forking a region category from
      // the restaurant screen can land it on the partner's list, and a partner
      // edit changes what the restaurant screen shows.
      invalidatesTags: (_result, error) => {
        if (error) {
          return [];
        }

        return [TAX_SCOPE.COMPANY, TAX_SCOPE.PROVIDER].map((id) => ({ type: TAX_CATEGORIES_TAGS.LIST, id }));
      },
    }),
    resetTaxCategory: builder.mutation({
      query: ({ apiManager, ...rest }) => apiManager.reset(rest),
      // Both screens, like update: a partner copy disappears from each.
      invalidatesTags: (_result, error) => {
        if (error) {
          return [];
        }

        return [TAX_SCOPE.COMPANY, TAX_SCOPE.PROVIDER].map((id) => ({ type: TAX_CATEGORIES_TAGS.LIST, id }));
      },
    }),
    deleteTaxCategory: builder.mutation({
      query: ({ apiManager, ...rest }) => apiManager.remove(rest),
      invalidatesTags: (_result, error, { scope }) => {
        if (error) {
          return [];
        }

        return [{ type: TAX_CATEGORIES_TAGS.LIST, id: scope }];
      },
    }),
  }),
  serializeQueryArgs,
});

export const {
  useGetTaxCategoriesQuery,
  useLazyGetTaxCategoryImpactQuery,
  useCreateTaxCategoryMutation,
  useUpdateTaxCategoryMutation,
  useResetTaxCategoryMutation,
  useDeleteTaxCategoryMutation,
} = taxCategoriesApiSlice;
